import React, { useState, useEffect, useRef, useContext } from 'react'
import { Form, Badge } from 'react-bootstrap'
import { useMutation, useQueryClient } from 'react-query'
import customColumnService from '../../../services/customColumnService'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPencilAlt } from '@fortawesome/free-solid-svg-icons'
import AuthContext from 'context/auth-context'
import './CustomColumnCell.css'

function CustomColumnCell({
  customColumn,
  transactionId,
  dashboardId,
  initialValue = '',
  customColumnValueId = null,
  canEdit = true
}) {
  const [isEditing, setIsEditing] = useState(false)
  const [isHovered, setIsHovered] = useState(false)
  const [value, setValue] = useState(initialValue || '')
  const [tempValue, setTempValue] = useState('')
  const inputRef = useRef(null)
  const queryClient = useQueryClient()
  const token = useContext(AuthContext)

  // Mutation for upserting custom column value
  const upsertMutation = useMutation(
    (data) =>
      customColumnService.upsertCustomColumnValue(token, dashboardId, data),
    {
      onSuccess: (data) => {
        setValue(data.value || '')
        setIsEditing(false)
        // Invalidate all custom column values queries for this dashboard
        queryClient.invalidateQueries({
          queryKey: ['customColumnValues', dashboardId],
          exact: false
        })
      },
      onError: () => {
        // Revert to original value on error
        setTempValue(value)
        setIsEditing(false)
      }
    }
  )

  // Update value when initialValue changes (when data is fetched)
  useEffect(() => {
    setValue(initialValue || '')
  }, [initialValue, customColumn.name, transactionId])

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus()

      // Handle different input types
      if (
        customColumn.column_type === 'text' ||
        customColumn.column_type === 'number'
      ) {
        inputRef.current.select()
      } else if (customColumn.column_type === 'date') {
        // For date inputs, show the picker immediately
        setTimeout(() => {
          if (inputRef.current) {
            inputRef.current.showPicker?.()
          }
        }, 50)
      }
    }
  }, [isEditing, customColumn.column_type])

  const handleStartEdit = () => {
    // Don't allow editing if user cannot edit
    if (!canEdit) {
      return
    }

    // Don't allow editing for select, boolean, and status types as they're always interactive
    if (
      customColumn.column_type === 'select' ||
      customColumn.column_type === 'boolean' ||
      customColumn.column_type === 'status'
    ) {
      return
    }

    setTempValue(value)
    setIsEditing(true)

    // For date types, we'll trigger the input opening in useEffect
    // This ensures the DOM element is ready before we try to interact with it
  }

  const handleSave = () => {
    if (tempValue !== value) {
      upsertMutation.mutate({
        custom_column_id: customColumn.id,
        transaction_id: transactionId,
        value: tempValue
      })
    } else {
      setIsEditing(false)
    }
  }

  const handleCancel = () => {
    setTempValue(value)
    setIsEditing(false)
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleSave()
    } else if (e.key === 'Escape') {
      handleCancel()
    }
  }

  const renderEditMode = () => {
    switch (customColumn.column_type) {
      case 'text':
        return (
          <div className="d-flex align-items-center">
            <Form.Control
              ref={inputRef}
              as="textarea"
              rows={3}
              value={tempValue}
              onChange={(e) => setTempValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  handleCancel()
                } else if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                  e.preventDefault()
                  handleSave()
                }
              }}
              onBlur={handleSave}
              size="sm"
              className="me-1"
              disabled={upsertMutation.isLoading}
              style={{ resize: 'vertical' }}
            />
            {upsertMutation.isLoading && (
              <span className="spinner-border spinner-border-sm ms-2" />
            )}
          </div>
        )

      case 'number':
        return (
          <div className="d-flex align-items-center">
            <Form.Control
              ref={inputRef}
              type="number"
              value={tempValue}
              onChange={(e) => setTempValue(e.target.value)}
              onKeyDown={handleKeyDown}
              onBlur={handleSave}
              size="sm"
              className="me-1"
              disabled={upsertMutation.isLoading}
            />
            {upsertMutation.isLoading && (
              <span className="spinner-border spinner-border-sm ms-2" />
            )}
          </div>
        )

      case 'date':
        return (
          <div className="d-flex align-items-center">
            <Form.Control
              ref={inputRef}
              type="date"
              value={tempValue}
              onChange={(e) => {
                setTempValue(e.target.value)
                // Auto-save for date
                upsertMutation.mutate({
                  custom_column_id: customColumn.id,
                  transaction_id: transactionId,
                  value: e.target.value
                })
              }}
              onKeyDown={handleKeyDown}
              onBlur={() => {
                // Only save if there's actually a change and we're not auto-saving
                if (tempValue !== value && !upsertMutation.isLoading) {
                  handleSave()
                } else {
                  setIsEditing(false)
                }
              }}
              size="sm"
              className="me-1"
              disabled={upsertMutation.isLoading}
            />
            {upsertMutation.isLoading && (
              <span className="spinner-border spinner-border-sm ms-2" />
            )}
          </div>
        )

      case 'boolean':
        return (
          <Form.Check
            ref={inputRef}
            type="checkbox"
            checked={tempValue === 'true' || tempValue === true}
            onChange={(e) => {
              const newValue = e.target.checked ? 'true' : 'false'
              setTempValue(newValue)
              // Auto-save for checkbox
              upsertMutation.mutate({
                custom_column_id: customColumn.id,
                transaction_id: transactionId,
                value: newValue
              })
            }}
            disabled={!canEdit || upsertMutation.isLoading}
          />
        )

      default:
        return null
    }
  }

  const renderDisplayMode = () => {
    // For boolean columns, show checkbox directly
    if (customColumn.column_type === 'boolean') {
      return (
        <div className="d-flex align-items-center justify-content-center">
          <Form.Check
            type="checkbox"
            checked={value === 'true' || value === true}
            onChange={(e) => {
              const newValue = e.target.checked ? 'true' : 'false'
              setValue(newValue)
              // Auto-save for checkbox
              upsertMutation.mutate({
                custom_column_id: customColumn.id,
                transaction_id: transactionId,
                value: newValue
              })
            }}
            disabled={!canEdit || upsertMutation.isLoading}
          />
        </div>
      )
    }

    // For select columns, render the select box directly
    if (customColumn.column_type === 'select') {
      const options = customColumn.settings?.options || []
      return (
        <div className="d-flex align-items-center">
          <Form.Select
            value={value}
            onChange={(e) => {
              setValue(e.target.value)
              // Auto-save for select
              upsertMutation.mutate({
                custom_column_id: customColumn.id,
                transaction_id: transactionId,
                value: e.target.value
              })
            }}
            size="sm"
            className="border-0 bg-transparent custom-select-display"
            disabled={!canEdit || upsertMutation.isLoading}
            style={{
              boxShadow: 'none',
              minWidth: '120px'
            }}
          >
            <option value=""></option>
            {options.map((option, index) => (
              <option key={index} value={option}>
                {option}
              </option>
            ))}
          </Form.Select>
          {upsertMutation.isLoading && (
            <span className="spinner-border spinner-border-sm ms-2" />
          )}
        </div>
      )
    }

    // For status columns, render a badge with status-specific styling
    if (customColumn.column_type === 'status') {
      const options = customColumn.settings?.options || [
        'To-do',
        'In progress',
        'Complete'
      ]

      // Badge styling based on status value
      const getBadgeVariant = (statusValue) => {
        switch (statusValue) {
          case 'To-do':
            return 'badge-secondary'
          case 'In progress':
            return 'badge-primary'
          case 'Complete':
            return 'badge-success'
          default:
            return 'badge-secondary'
        }
      }

      return (
        <div className="d-flex align-items-center position-relative">
          <Form.Select
            value={value}
            onChange={(e) => {
              setValue(e.target.value)
              // Auto-save for status
              upsertMutation.mutate({
                custom_column_id: customColumn.id,
                transaction_id: transactionId,
                value: e.target.value
              })
            }}
            size="sm"
            className="border-0 bg-transparent custom-select-display status-select"
            disabled={!canEdit || upsertMutation.isLoading}
            style={{
              boxShadow: 'none',
              minWidth: '120px',
              color: 'transparent'
            }}
          >
            <option value=""></option>
            {options.map((option, index) => (
              <option key={index} value={option}>
                {option}
              </option>
            ))}
          </Form.Select>

          {/* Badge overlay that shows the selected value */}
          {value && (
            <div
              className="position-absolute d-flex align-items-center"
              style={{
                left: '8px',
                top: '50%',
                transform: 'translateY(-50%)',
                pointerEvents: 'none',
                zIndex: 1
              }}
            >
              <Badge className={getBadgeVariant(value)}>{value}</Badge>
            </div>
          )}

          {upsertMutation.isLoading && (
            <span className="spinner-border spinner-border-sm ms-2" />
          )}
        </div>
      )
    }

    let displayValue = value

    // eslint-disable-next-line default-case
    switch (customColumn.column_type) {
      case 'date':
        if (value) {
          try {
            // Parse YYYY-MM-DD format and create date in local timezone
            const [year, month, day] = value.split('-').map(Number)
            const date = new Date(year, month - 1, day)
            displayValue = date.toLocaleDateString()
          } catch (e) {
            displayValue = value
          }
        }
        break
    }

    return (
      <div
        className="d-flex align-items-center custom-column-cell"
        onClick={canEdit ? handleStartEdit : undefined}
        style={{
          cursor: canEdit ? 'pointer' : 'default',
          minHeight: '25px',
          position: 'relative'
        }}
        role={canEdit ? "button" : undefined}
        tabIndex={canEdit ? 0 : undefined}
        onMouseEnter={() => canEdit && setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onKeyDown={canEdit ? (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            handleStartEdit()
          }
        } : undefined}
      >
        <span
          className="flex-grow-1"
          style={
            customColumn.column_type === 'text'
              ? { wordBreak: 'break-word', overflowWrap: 'break-word' }
              : undefined
          }
        >
          {displayValue}
        </span>
        {isHovered && canEdit && (
          <FontAwesomeIcon
            icon={faPencilAlt}
            className="text-muted ms-1"
            style={{ fontSize: '0.75rem' }}
          />
        )}
      </div>
    )
  }

  return (
    <td
      className="align-middle custom-column-cell-wrapper position-relative"
      style={
        customColumn.column_type === 'text'
          ? { minWidth: '200px', maxWidth: '400px', width: '400px', whiteSpace: 'normal', overflow: 'hidden' }
          : undefined
      }
    >
      {isEditing &&
      customColumn.column_type !== 'select' &&
      customColumn.column_type !== 'boolean' &&
      customColumn.column_type !== 'status'
        ? renderEditMode()
        : renderDisplayMode()}
    </td>
  )
}

export default CustomColumnCell
